import { useState, useEffect, useRef } from "react";
import Guideline from "./components/Guideline";
import CheckQuality from "./components/CheckQuality";
import Verifying from "./components/Verifying";
import mykadFrontGood from "../../assets/images/guideline-ic-front-good.jpg";
import mykadFrontBad from "../../assets/images/guideline-ic-front-bad.jpg";
import mykadBackGood from "../../assets/images/guideline-ic-back-good.jpg";
import mykadBackBad from "../../assets/images/guideline-ic-back-bad.jpg";
import TakePhotoError from "./components/TakePhotoError";
import { useLocation } from "react-router-dom";
import InvalidToken from "./components/InvalidToken";
import { useMyKadService } from "./useMyKadService";
import UploadPhoto from "./components/UploadPhoto";
import MyKadReject from "./components/MyKadReject";
import MultipleReject from "./components/MultipleReject";
import GuidelineSelfieWithMyKad from "./components/GuidelineSelfieWithMyKad";
import CheckQualitySelfieWithMyKad from "./components/CheckQualitySelfieWithMyKad";
import GuidelineHologramDetection from "./components/GuidelineHologramDetection";
import CheckQualityHologramDetection from "./components/CheckQualityHologramDetection";
import ExceedMaxRetry from "./components/ExceedMaxRetry";
import EditNameModal from "./components/EditNameModal";
import EditIDNumberModal from "./components/EditIDNumberModal";
import VerificationTimeout from "./components/VerificationTimeout";
import { timeoutRetryStep } from "./timeoutRetryStep";

const MyKad = ({ validation, onComplete, onSessionUpdate }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token") || sessionStorage.getItem("ekycSessionToken");

  const {
    extractNameAndIDNumber,
    uploadFront,
    verifyFront,
    uploadBack,
    verifyBack,
    verifySelfieWithMyKad,
    verifyHologram,
  } = useMyKadService();

  const [step, setStep] = useState("mykad-front-guideline");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [validationID, setValidationID] = useState(null);

  const [progressStates, setProgressStates] = useState({
    myKadFront: "Not Started", // Not Started, Fail, Pass
    myKadBack: "Not Started",
    blacklist: "Not Started",
    kenalID: "Not Started",
    selfieWithMyKad: "Not Started",
    hologramDetection: "Not Started",
  });

  const [workflowConfig, setWorkflowConfig] = useState({});
  const [userUploadImageBuffer, setUserUploadImageBuffer] = useState(null);
  const [myKadFrontOriginalImageBlob, setMyKadFrontOriginalImageBlob] =
    useState(null);
  const [myKadFrontImageBlob, setMyKadFrontImageBlob] = useState(null);
  const [myKadBackOriginalImageBlob, setMyKadBackOriginalImageBlob] =
    useState(null);
  const [myKadBackImageBlob, setMyKadBackImageBlob] = useState(null);
  const [myKadHologramImageBlob, setMyKadHologramImageBlob] = useState(null);
  const [myKadHologramOriginalImageBlob, setMyKadHologramOriginalImageBlob] =
    useState(null);
  const [autofillFromOCR, setAutofillFromOCR] = useState(false);
  const [ocrName, setOcrName] = useState("");
  const [ocrIDNumber, setOcrIDNumber] = useState("");
  const [editNameModal, setEditNameModal] = useState(false);
  const [editIDNumberModal, setEditIDNumberModal] = useState(false);

  const [takePhotoErrorMessage, setTakePhotoErrorMessage] = useState("");

  const [myKadUIConfig, setMyKadUIConfig] = useState({});

  // Track upload-step timeout
  const uploadTimeoutRef = useRef(null);
  const [uploadTimeoutRetryStep, setUploadTimeoutRetryStep] = useState(
    "mykad-front-guideline"
  );
  const [uploadCancelled, setUploadCancelled] = useState(false);
  const uploadCancelledRef = useRef(false);
  // Which kind of timeout sent the user to the verification-timeout page:
  // "scan" for the client-side document scanning timer, "server" for server calls.
  const [verificationTimeoutType, setVerificationTimeoutType] =
    useState("server");
  // Tracks what the 50s upload timer is currently guarding so it reports the
  // right cause if it fires: "scan" during local Scanbot scanning, flipped to
  // "server" once the front step is awaiting the OCR-extract server call.
  const uploadTimeoutTypeRef = useRef("scan");
  // Which screen timed out, for where "Try Again" goes: "upload" (the 50s upload timer or the
  // OCR-extract call) or "verifying" (the Verifying screen's 2-minute timeout).
  const [timeoutCause, setTimeoutCause] = useState("upload");
  // The side statuses a verifying timeout reported (from its deciding check-result), for where
  // "Try Again" goes. Kept apart from progressStates so the step routing does not act on them.
  const [verifyingTimeoutStates, setVerifyingTimeoutStates] = useState(progressStates);

  useEffect(() => {
    if (uploadTimeoutRef.current) {
      clearTimeout(uploadTimeoutRef.current);
      uploadTimeoutRef.current = null;
    }

    if (step === "mykad-front-upload") {
      setUploadCancelled(false);
      uploadCancelledRef.current = false;
      uploadTimeoutTypeRef.current = "scan";
      setUploadTimeoutRetryStep("mykad-front-guideline");
      uploadTimeoutRef.current = setTimeout(() => {
        setUploadCancelled(true);
        uploadCancelledRef.current = true;
        setVerificationTimeoutType(uploadTimeoutTypeRef.current);
        setTimeoutCause("upload");
        setStep("verification-timeout");
      }, 50000);
    } else if (step === "mykad-back-upload") {
      setUploadCancelled(false);
      uploadCancelledRef.current = false;
      uploadTimeoutTypeRef.current = "scan";
      setUploadTimeoutRetryStep("mykad-back-guideline");
      uploadTimeoutRef.current = setTimeout(() => {
        setUploadCancelled(true);
        uploadCancelledRef.current = true;
        setVerificationTimeoutType(uploadTimeoutTypeRef.current);
        setTimeoutCause("upload");
        setStep("verification-timeout");
      }, 50000);
    }

    return () => {
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
        uploadTimeoutRef.current = null;
      }
    };
  }, [step]);

  useEffect(() => {
    if (validation) {
      const myKadResults =
        validation?.ComponentResults?.[validation.CurrentStep.ID] || {};
      const blacklistResult =
        validation?.ComponentResults?.[
          validation.CurrentStep.ID + "_Blacklist"
        ] || {};
      const kenalIDResult =
        validation?.ComponentResults?.[
          validation.CurrentStep.ID + "_KenalID"
        ] || {};
      const selfieWithMyKadResult =
        validation?.ComponentResults?.[
          validation.CurrentStep.ID + "_SelfieWithMyKad"
        ] || {};
      const hologramDetectionResult =
        validation?.ComponentResults?.[
          validation.CurrentStep.ID + "_MyKadHologram"
        ] || {};
      setProgressStates({
        myKadFront: myKadResults?.MyKadFrontResult
          ? myKadResults.MyKadFrontResult
          : "Not Started",
        myKadBack: myKadResults?.MyKadBackResult
          ? myKadResults.MyKadBackResult
          : "Not Started",
        blacklist: blacklistResult?.Result
          ? blacklistResult.Result
          : "Not Started",
        kenalID: kenalIDResult?.Result ? kenalIDResult.Result : "Not Started",
        selfieWithMyKad: selfieWithMyKadResult?.Result
          ? selfieWithMyKadResult.Result
          : "Not Started",
        hologramDetection: hologramDetectionResult?.Result
          ? hologramDetectionResult.Result
          : "Not Started",
      });

      const config = validation?.WorkflowConfigSnapshot?.Config?.[
        validation.CurrentStep.ID
      ].MyKadWorkflowConfig.IDVerification.reduce((acc, item) => {
        acc[item.name] = item["enabled"];
        return acc;
      }, {});

      // Extract UI config from validation
      const uiConfig = validation?.WorkflowConfigSnapshot?.Config?.[
        validation.CurrentStep.ID
      ]?.MyKadUIConfig;
      setMyKadUIConfig(uiConfig);
      setWorkflowConfig(config);
      setAutofillFromOCR(config["Autofill From OCR"]);
      setValidationID(validation?.ValidationID);
    }
  }, [validation]);

  useEffect(() => {
    // Check if exceeded max retry first - this takes priority over other conditions
    // Allow "Pass" and "In Review" to proceed
    const overallResult = validation?.ComponentResults?.[validation.CurrentStep.ID]?.Result;
    if (
      validation?.ComponentResults?.[validation.CurrentStep.ID]
        ?.VALIDATION_COUNT >= 3 && 
      overallResult !== "Pass" &&
      overallResult !== "In Review"
    ) {
      return setStep("exceed-max-retry");
    }

    // If overall result is "In Review", proceed to next step (treat like Pass for flow purposes)
    if (overallResult === "In Review") {
      if (onComplete) {
        onComplete({
          validationID: validation?.ValidationID,
        });
      }
      return;
    }

    if (
      progressStates.myKadFront === "Pending" ||
      progressStates.myKadBack === "Pending"
    ) {
      setStep("verifying");
    } else if (progressStates.myKadFront === "Not Started") {
      setStep("mykad-front-guideline");
    } else if (progressStates.myKadFront === "Fail") {
      setStep("mykad-front-rejected");
    } else if (progressStates.myKadBack === "Not Started") {
      setStep("mykad-back-guideline");
    } else if (progressStates.myKadBack === "Fail") {
      setStep("mykad-back-rejected");
    } else if (progressStates.blacklist === "Fail") {
      setStep("blacklist-rejected");
    } else if (progressStates.kenalID === "Fail") {
      setStep("kenal-id-rejected");
    } else if (
      progressStates.selfieWithMyKad === "Not Started" &&
      workflowConfig["Selfie With MyKad"]
    ) {
      setStep("selfie-with-mykad-guideline");
    } else if (progressStates.selfieWithMyKad === "Fail") {
      setStep("selfie-with-mykad-rejected");
    } else if (
      progressStates.hologramDetection === "Not Started" &&
      workflowConfig["Hologram Detection"]
    ) {
      setStep("hologram-detection-guideline");
    } else if (progressStates.hologramDetection === "Fail") {
      setStep("hologram-detection-rejected");
    } else {
      if (onComplete) {
        onComplete({
          validationID: validation?.ValidationID,
        });
      }
    }
  }, [progressStates, workflowConfig, validation]);

  const onNext = (currentStep) => {
    // From current step, determine which step to go next, based on workflow config and also progressStates.

    if (currentStep === "myKadFront") {
      if (
        progressStates.myKadBack === "Fail" ||
        progressStates.myKadBack === "Not Started"
      ) {
        setStep("mykad-back-guideline");
      } else if (
        workflowConfig["Selfie With MyKad"] &&
        (progressStates.selfieWithMyKad === "Fail" ||
          progressStates.selfieWithMyKad === "Not Started")
      ) {
        setStep("selfie-with-mykad-guideline");
      } else if (
        workflowConfig["Hologram Detection"] &&
        (progressStates.hologramDetection === "Fail" ||
          progressStates.hologramDetection === "Not Started")
      ) {
        setStep("hologram-detection-guideline");
      } else {
        setStep("verifying");
      }
    } else if (currentStep === "myKadBack") {
      if (
        workflowConfig["Selfie With MyKad"] &&
        (progressStates.selfieWithMyKad === "Fail" ||
          progressStates.selfieWithMyKad === "Not Started")
      ) {
        setStep("selfie-with-mykad-guideline");
      } else if (
        workflowConfig["Hologram Detection"] &&
        (progressStates.hologramDetection === "Fail" ||
          progressStates.hologramDetection === "Not Started")
      ) {
        setStep("hologram-detection-guideline");
      } else {
        setStep("verifying");
      }
    } else if (currentStep === "selfieWithMyKad") {
      if (
        workflowConfig["Hologram Detection"] &&
        (progressStates.hologramDetection === "Fail" ||
          progressStates.hologramDetection === "Not Started")
      ) {
        setStep("hologram-detection-guideline");
      } else {
        setStep("verifying");
      }
    } else if (currentStep === "hologramDetection") {
      setStep("verifying");
    }
  };

  const renderSteps = () => {
    switch (step) {
      case "mykad-front-guideline":
        return (
          <Guideline
            myKadSide="front"
            positiveSampleImage={mykadFrontGood}
            negativeSampleImage={mykadFrontBad}
            onNext={(imageBuffer) => {
              setUserUploadImageBuffer(imageBuffer);
              setStep("mykad-front-upload");
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "mykad-front-upload":
        return (
          <UploadPhoto
            cancelUpload={uploadCancelled}
            useOCRForData={autofillFromOCR}
            imageBuffer={userUploadImageBuffer}
            onSuccess={ async ({
              croppedImageBlob,
              originalImageBlob,
              // ocrName,
              // ocrIDNumber,
            }) => {
              if (uploadCancelledRef.current) return;
              setMyKadFrontImageBlob(croppedImageBlob);
              setMyKadFrontOriginalImageBlob(originalImageBlob);

              if (autofillFromOCR === true) {
                const formData = new FormData();
                formData.append("file", croppedImageBlob);
                formData.append("validationID", validation?.ValidationID || validationID);
                // Scanning is done; now awaiting the server. If the 50s timer
                // fires past this point it's a server timeout, not a scan one.
                uploadTimeoutTypeRef.current = "server";
                const res = await extractNameAndIDNumber(formData);
                if (uploadCancelledRef.current) return;
                if (res.status === "timeout") {
                  setUploadCancelled(true);
                  uploadCancelledRef.current = true;
                  setVerificationTimeoutType("server");
                  setTimeoutCause("upload");
                  setStep("verification-timeout");
                  return;
                }
                if (res.status === "success") {
                  setOcrName(res.data.name);
                  setOcrIDNumber(res.data.idNumber);
                }
              }
              if (uploadCancelledRef.current) return;
              setStep("mykad-front-check-quality");
            }}
            onError={(error, showImage = true) => {
              if (uploadCancelledRef.current) return;
              setTakePhotoErrorMessage({ text: error, showImage });
              setStep("mykad-front-upload-error");
            }}
          />
        );
      case "mykad-front-upload-error":
        return (
          <TakePhotoError
            errorMessage={takePhotoErrorMessage.text}
            showImage={takePhotoErrorMessage.showImage}
            imageBuffer={userUploadImageBuffer}
            onRetry={() => {
              setTakePhotoErrorMessage("");
              setStep("mykad-front-guideline");
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "mykad-front-check-quality":
        return (
          <CheckQuality
            useOCRForData={autofillFromOCR}
            ocrName={ocrName}
            ocrIDNumber={ocrIDNumber}
            isSubmitting={isSubmitting}
            myKadSide="front"
            imageBlob={myKadFrontImageBlob}
            onRetake={(imageBuffer) => {
              setUserUploadImageBuffer(imageBuffer);
              setStep("mykad-front-upload");
            }}
            onConfirm={() => {
              setIsSubmitting(true);

              const formData = new FormData();
              formData.append("validationID", validationID);
              formData.append("autofillFromOCR", autofillFromOCR);
              formData.append("originalImage", myKadFrontOriginalImageBlob);
              formData.append("croppedImage", myKadFrontImageBlob);

              if (autofillFromOCR === true) {
                formData.append("ocrName", ocrName);
                formData.append("ocrIDNumber", ocrIDNumber);
              }

              uploadFront(formData)
                .then((res) => {
                  onSessionUpdate?.(res.session);
                  if (res.status === "success") {
                    // Submitting for verification, and proceed to mykad back immediately
                    verifyFront({
                      validationID,
                    }).then((verifyRes) => {
                      onSessionUpdate?.(verifyRes.session);
                    });
                    onNext("myKadFront");
                  }
                  setIsSubmitting(false);
                })
                .catch(() => {
                  setIsSubmitting(false);
                });
            }}
            onEditName={() => {
              setEditNameModal(true);
            }}
            onEditIDNumber={() => {
              setEditIDNumberModal(true);
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "mykad-front-rejected":
        return (
          <MyKadReject
            validationID={validationID}
            myKadSide="front"
            onRetry={() => setStep("mykad-front-guideline")}
            uiConfig={myKadUIConfig}
          />
        );
      case "mykad-back-guideline":
        return (
          <Guideline
            myKadSide="back"
            positiveSampleImage={mykadBackGood}
            negativeSampleImage={mykadBackBad}
            onNext={(imageBuffer) => {
              setUserUploadImageBuffer(imageBuffer);
              setStep("mykad-back-upload");
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "mykad-back-upload":
        return (
          <UploadPhoto
            cancelUpload={uploadCancelled}
            imageBuffer={userUploadImageBuffer}
            onSuccess={({ croppedImageBlob, originalImageBlob }) => {
              if (uploadCancelledRef.current) return;
              setMyKadBackImageBlob(croppedImageBlob);
              setMyKadBackOriginalImageBlob(originalImageBlob);
              if (uploadCancelledRef.current) return;
              setStep("mykad-back-check-quality");
            }}
            onError={(error, showImage = true) => {
              if (uploadCancelledRef.current) return;
              setTakePhotoErrorMessage({ text: error, showImage });
              setStep("mykad-back-upload-error");
            }}
          />
        );
      case "mykad-back-upload-error":
        return (
          <TakePhotoError
            errorMessage={takePhotoErrorMessage.text}
            showImage={takePhotoErrorMessage.showImage}
            imageBuffer={userUploadImageBuffer}
            onRetry={() => {
              setTakePhotoErrorMessage("");
              setStep("mykad-back-guideline");
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "mykad-back-check-quality":
        return (
          <CheckQuality
            isSubmitting={isSubmitting}
            myKadSide="back"
            imageBlob={myKadBackImageBlob}
            onRetake={(imageBuffer) => {
              setUserUploadImageBuffer(imageBuffer);
              setStep("mykad-back-upload");
            }}
            onConfirm={() => {
              setIsSubmitting(true);

              const formData = new FormData();
              formData.append("validationID", validationID);
              formData.append("originalImage", myKadBackOriginalImageBlob);
              formData.append("croppedImage", myKadBackImageBlob);

              uploadBack(formData)
                .then((res) => {
                  onSessionUpdate?.(res.session);
                  if (res.status === "success") {
                    // Submitting for verification, and proceed to verifiying screen immediately
                    verifyBack({
                      validationID,
                    }).then((verifyRes) => {
                      onSessionUpdate?.(verifyRes.session);
                    });

                    onNext("myKadBack");
                  }
                  setIsSubmitting(false);
                })
                .catch(() => {
                  setIsSubmitting(false);
                });
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "mykad-back-rejected":
        return (
          <MyKadReject
            validationID={validationID}
            myKadSide="back"
            onRetry={() => setStep("mykad-back-guideline")}
            uiConfig={myKadUIConfig}
          />
        );
      case "blacklist-rejected":
        return (
          <MyKadReject
            validationID={validationID}
            myKadSide="blacklist"
            onRetry={() => setStep("mykad-front-guideline")}
            uiConfig={myKadUIConfig}
          />
        );
      case "kenal-id-rejected":
        return (
          <MyKadReject
            validationID={validationID}
            myKadSide="kenalID"
            onRetry={() => setStep("mykad-front-guideline")}
            uiConfig={myKadUIConfig}
          />
        );
      case "selfie-with-mykad-guideline":
        return (
          <GuidelineSelfieWithMyKad
            onNext={(imageBuffer) => {
              setUserUploadImageBuffer(imageBuffer);
              setStep("selfie-with-mykad-check-quality");
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "selfie-with-mykad-check-quality":
        return (
          <CheckQualitySelfieWithMyKad
            isSubmitting={isSubmitting}
            imageBuffer={userUploadImageBuffer}
            onRetake={(imageBuffer) => {
              setUserUploadImageBuffer(imageBuffer);
              setStep("selfie-with-mykad-check-quality");
            }}
            onConfirm={(imageBlob) => {
              setIsSubmitting(true);
              const formData = new FormData();
              formData.append("validationID", validationID);
              formData.append("file", imageBlob);

              verifySelfieWithMyKad(formData)
                .then((res) => {
                  onSessionUpdate?.(res.session);
                  onNext("selfieWithMyKad");
                  setIsSubmitting(false);
                })
                .catch(() => {
                  setIsSubmitting(false);
                });
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "selfie-with-mykad-rejected":
        return (
          <MyKadReject
            validationID={validationID}
            myKadSide="selfieWithMyKad"
            onRetry={() => setStep("selfie-with-mykad-guideline")}
            uiConfig={myKadUIConfig}
          />
        );
      case "hologram-detection-guideline":
        return (
          <GuidelineHologramDetection
            onNext={(imageBuffer) => {
              setUserUploadImageBuffer(imageBuffer);
              setStep("hologram-detection-upload");
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "hologram-detection-upload":
        return (
          <UploadPhoto
            imageBuffer={userUploadImageBuffer}
            onSuccess={({ croppedImageBlob, originalImageBlob }) => {
              setMyKadHologramImageBlob(croppedImageBlob);
              setMyKadHologramOriginalImageBlob(originalImageBlob);
              setStep("hologram-detection-check-quality");
            }}
            onError={(error) => {
              setTakePhotoErrorMessage(error);
              setStep("hologram-detection-upload-error");
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "hologram-detection-upload-error":
        return (
          <TakePhotoError
            errorMessage={takePhotoErrorMessage}
            imageBuffer={userUploadImageBuffer}
            onRetry={() => {
              setTakePhotoErrorMessage("");
              setStep("hologram-detection-guideline");
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "hologram-detection-check-quality":
        return (
          <CheckQualityHologramDetection
            isSubmitting={isSubmitting}
            imageBuffer={myKadHologramImageBlob}
            onRetake={(imageBuffer) => {
              setUserUploadImageBuffer(imageBuffer);
              setStep("hologram-detection-upload");
            }}
            onConfirm={() => {
              setIsSubmitting(true);

              const formData = new FormData();
              formData.append("validationID", validationID);
              formData.append("croppedImage", myKadHologramImageBlob);
              formData.append("originalImage", myKadHologramOriginalImageBlob);

              verifyHologram(formData)
                .then((res) => {
                  onSessionUpdate?.(res.session);
                  onNext("hologramDetection");
                  setIsSubmitting(false);
                })
                .catch(() => {
                  setIsSubmitting(false);
                });
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "hologram-detection-rejected":
        return (
          <MyKadReject
            validationID={validationID}
            myKadSide="hologramDetection"
            onRetry={() => setStep("hologram-detection-guideline")}
            uiConfig={myKadUIConfig}
          />
        );
      case "verifying":
        return (
          <Verifying
            validationID={validationID}
            progressStates={progressStates}
            onSessionUpdate={onSessionUpdate}
            onPass={(checkResult) => {
              // If "In Review", proceed directly to next workflow step
              if (checkResult.overallStatus === "In Review") {
                onComplete({ validationID: validation?.ValidationID });
                return;
              }
              setProgressStates((prev) => ({
                ...prev,
                myKadFront: checkResult.front.status,
                myKadBack: checkResult.back.status,
                selfieWithMyKad: checkResult.selfie
                  ? checkResult.selfie
                  : prev.selfieWithMyKad,
                hologramDetection: checkResult.hologram
                  ? checkResult.hologram
                  : prev.hologramDetection,
              }));
              onNext("verifying");
            }}
            onFail={(checkResult) => {
              if (checkResult.isTimeout) {
                setVerificationTimeoutType("server");
                setTimeoutCause("verifying");
                setVerifyingTimeoutStates({
                  ...progressStates,
                  ...checkResult.sideStatuses,
                });
                return setStep("verification-timeout");
              }

              if (checkResult.validationCount >= 3) {
                return setStep("exceed-max-retry");
              }

              setTimeout(() => {
                setProgressStates((prev) => ({
                  ...prev,
                  myKadFront: checkResult.front.status,
                  myKadBack: checkResult.back.status,
                  blacklist: checkResult.blacklist
                    ? checkResult.blacklist
                    : prev.blacklist,
                  kenalID: checkResult.kenalID
                    ? checkResult.kenalID
                    : prev.kenalID,
                  selfieWithMyKad: checkResult.selfie
                    ? checkResult.selfie
                    : prev.selfieWithMyKad,
                  hologramDetection: checkResult.hologram
                    ? checkResult.hologram
                    : prev.hologramDetection,
                }));
              }, 2000);
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "multiple-reject":
        return (
          <MultipleReject
            progressStates={progressStates}
            onRetry={() => {
              if (progressStates.myKadFront === "Fail") {
                setStep("mykad-front-guideline");
              } else if (progressStates.myKadBack === "Fail") {
                setStep("mykad-back-guideline");
              } else if (progressStates.selfieWithMyKad === "Fail") {
                setStep("selfie-with-mykad-guideline");
              } else if (progressStates.hologramDetection === "Fail") {
                setStep("hologram-detection-guideline");
              } else if (progressStates.blacklist === "Fail") {
                setStep("mykad-front-guideline");
              } else if (progressStates.kenalID === "Fail") {
                setStep("mykad-front-guideline");
              }
            }}
            uiConfig={myKadUIConfig}
          />
        );
      case "exceed-max-retry":
        return <ExceedMaxRetry />;
      case "verification-timeout":
        return (
          <VerificationTimeout
            onRetry={() => {
              setUploadCancelled(false);
              uploadCancelledRef.current = false;
              setStep(
                timeoutRetryStep({
                  cause: timeoutCause,
                  uploadRetryStep: uploadTimeoutRetryStep,
                  progressStates: verifyingTimeoutStates,
                  workflowConfig,
                })
              );
            }}
            timeoutType={verificationTimeoutType}
            uiConfig={myKadUIConfig}
          />
        );
      case "invalid-token":
        return <InvalidToken />;
      default:
        // Return loading screen
        return <div>Loading...</div>;
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 flex justify-center items-center">
      <div className="min-h-screen w-full max-w-md bg-transparent relative md:rounded-xl md:shadow-lg">
        {!token ? <InvalidToken /> : renderSteps()}

        <EditNameModal
          isOpen={editNameModal}
          initialName={ocrName}
          onClose={() => setEditNameModal(false)}
          onSave={(newName) => {
            setOcrName(newName);
            setEditNameModal(false);
          }}
        />

        <EditIDNumberModal
          isOpen={editIDNumberModal}
          initialIDNumber={ocrIDNumber}
          onClose={() => setEditIDNumberModal(false)}
          onSave={(newIDNumber) => {
            setOcrIDNumber(newIDNumber);
            setEditIDNumberModal(false);
          }}
        />
      </div>
    </div>
  );
};

export default MyKad;
